import React, { FC } from 'react';
import { useVitalMetric } from '@funtech-int/yango-shared';

import { PlayerSkinErrorBoundary } from '@/scenarios/legacy-player/components/PlayerSkin/PlayerSkinErrorBoundary';
import { playerChannelFirstFrame } from '@/scenarios/player/lib/metrics';

import { useSaveLastWatchedChannel } from '../../hooks/use-save-last-watched-channel';
import { Player, type PlayerConfigProps } from '../../../Player/Player';
import { PlayerTracker } from '../../../feature-player/ui/player-tracker/component';

import { ChannelPlayerController } from './ChannelPlayerController';
import { ChannelPlayerTracker } from './ChannelPlayerTracker';
import { ChannelPlayerView } from './ChannelPlayerView';

interface ChannelPlayerProps {
    channelId: string;
    /** Default: true */
    hasSkin?: boolean;
    onStart?: (contentId: string) => void;
    onClose?: () => void;
    onDurationChange?: (duration: number) => void;
    isPromo?: boolean;
}

export const ChannelPlayer: FC<ChannelPlayerProps> = ({
    onStart,
    isPromo,
    channelId,
    onClose,
    hasSkin,
    onDurationChange = (): void => undefined,
}) => {
    const saveLastWatchedChannel = useSaveLastWatchedChannel();
    const firstFrameDuration = useVitalMetric(playerChannelFirstFrame);

    const handleStart: PlayerConfigProps['onStart'] = (contentId) => {
        if (!isPromo) {
            saveLastWatchedChannel({ contentId });
        }

        firstFrameDuration.end();

        onStart?.(contentId);
    };

    if (!channelId) {
        return null;
    }

    return (
        <PlayerSkinErrorBoundary>
            {/* eslint-disable-next-line react/no-unused-prop-types */}
            {({ forceDefaultSkin }: { forceDefaultSkin: boolean }) => (
                <Player
                    contentId={channelId}
                    onStart={handleStart}
                    onClose={onClose}
                    onBeforeInit={firstFrameDuration.begin}
                    renderPlayer={({
                        contentId,
                        psid,
                        onStartCallback,
                        handleError,
                        handleFinish,
                        onClose = (): void => undefined,
                    }) => (
                        <PlayerTracker>
                            <ChannelPlayerController
                                onContentImpression={onStartCallback}
                                onFinish={handleFinish}
                                onError={handleError}
                                onDurationChanged={onDurationChange}
                            />
                            <ChannelPlayerTracker contentId={contentId} psid={psid} />
                            <ChannelPlayerView
                                contentId={contentId}
                                onClose={onClose}
                                psid={psid}
                                withSkin={(hasSkin ?? true) && !forceDefaultSkin}
                            />
                        </PlayerTracker>
                    )}
                />
            )}
        </PlayerSkinErrorBoundary>
    );
};
